import React from 'react';
import { Field, Text, Checkbox, RadioGroup, SectionShell } from './FormControls';

const ITEMS = [
  { key: 'externalSurfacesExamined', label: 'External surfaces and welds cleaned and examined' },
  { key: 'internalSurfacesExamined', label: 'Internal surfaces and welds examined via openings' },
  { key: 'allValvesFittingsExamined', label: 'All valves, fittings and ancillary equipment examined' },
  { key: 'reliefValvesReplacedRainCaps', label: 'Relief valve(s) replaced and fitted with rain caps' },
  { key: 'reliefValveAdaptorChecked', label: 'Relief valve adaptor/manifold — checked' },
  { key: 'reliefValveAdaptorReplaced', label: 'Relief valve adaptor/manifold — replaced' },
  { key: 'fixedLiquidLevelChecked', label: 'Fixed liquid level checked' },
  { key: 'floatGaugeScrewsChecked', label: 'Float gauge and screws checked' },
  { key: 'hoodServiceableLockable', label: 'Hood serviceable and lockable' },
  { key: 'allConnectionsCheckedLeaks', label: 'All connections checked for leaks' },
  { key: 'vapourOffTakeValveOperational', label: 'Vapour off-take valve operational check' },
  { key: 'acmeFillerValveVisuallyChecked', label: 'Acme filler valve — visually checked' },
  { key: 'newFillerSealingWasherFitted', label: 'New filler sealing washer fitted and cap satisfactory' },
  { key: 'manualShutoffChecked', label: 'Manual shut-off valve checked' },
  { key: 'pressureGaugeChecked', label: 'Pressure gauge checked' },
  { key: 'gaugeCalibrationChecked', label: 'Gauge calibration checked' },
  { key: 'tankCompoundLabelsSatisfactory', label: 'Tank and compound labels satisfactory' },
];

// Items shown only when the parent decides they apply (see conditionalVisibility).
const CONDITIONAL_ITEMS = [
  { key: 'coatingHolidayCheck', label: "Coating 'Holiday' check (U/G vessels only)" },
  { key: 'foundationsSatisfactory', label: 'Foundations satisfactory' },
  { key: 'hoseIfFittedExamined', label: 'Hose, if fitted, examined' },
  { key: 'siteConditionsSatisfactory', label: 'Site conditions satisfactory' },
];

const ChecklistSection = ({ data, update, conditionalVisibility = {} }) => (
  <SectionShell title="Inspection Checklist" description="Tick every item that passes; answer the hydraulic test question">
    <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-3">
      {ITEMS.map((item) => {
        // Gauge calibration is Not Applicable when no hydraulic test is done.
        const naNoHydraulic = item.key === 'gaugeCalibrationChecked' && data.hydraulicTest === 'no';
        return (
          <Checkbox
            key={item.key}
            label={naNoHydraulic ? `${item.label} (N/A — no hydraulic test)` : item.label}
            checked={naNoHydraulic ? false : data[item.key]}
            disabled={naNoHydraulic}
            onChange={(v) => update(item.key, v)}
          />
        );
      })}
      {CONDITIONAL_ITEMS.filter((item) => conditionalVisibility[item.key]).map((item) => (
        <Checkbox
          key={item.key}
          label={item.label}
          checked={data[item.key]}
          onChange={(v) => update(item.key, v)}
        />
      ))}
    </div>

    <Field label="Tank checked for corrosion and found to be" required>
      <RadioGroup
        name="corrosionState"
        value={data.corrosionState}
        onChange={(v) => update('corrosionState', v)}
        options={[
          { value: 'satisfactory', label: 'Satisfactory' },
          { value: 'surface', label: 'Surface corrosion' },
          { value: 'severe', label: 'Severely corroded' },
        ]}
      />
    </Field>

    <Field label="Hydraulic test" required>
      <RadioGroup
        name="hydraulicTest"
        value={data.hydraulicTest}
        onChange={(v) => {
          update('hydraulicTest', v);
          if (v !== 'yes') {
            update('hydraulicTestPressure', '');
          }
          if (v === 'no') {
            update('gaugeCalibrationChecked', false);
          }
        }}
        options={[
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ]}
      />
    </Field>

    {data.hydraulicTest === 'yes' && (
      <Field label="Hydraulic Test Pressure (BARG)" required>
        <Text
          type="number"
          value={data.hydraulicTestPressure}
          onChange={(v) => update('hydraulicTestPressure', v)}
          placeholder="e.g. 24"
          required
        />
      </Field>
    )}

    <Field label="Does the Paintwork Meet Requirements?" required>
      <RadioGroup
        name="paintworkMeetsRequirements"
        value={data.paintworkMeetsRequirements}
        onChange={(v) => {
          update('paintworkMeetsRequirements', v);
          if (v !== 'no') {
            update('paintworkRequiresRepaint', false);
            update('paintworkRequiresCleaning', false);
          }
        }}
        options={[
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ]}
      />
    </Field>

    {data.paintworkMeetsRequirements === 'no' && (
      <Field label="Tank Paintwork Requires" required>
        <div className="flex flex-col gap-2">
          <Checkbox
            label="Repaint"
            checked={data.paintworkRequiresRepaint}
            onChange={(v) => update('paintworkRequiresRepaint', v)}
          />
          <Checkbox
            label="Cleaning"
            checked={data.paintworkRequiresCleaning}
            onChange={(v) => update('paintworkRequiresCleaning', v)}
          />
        </div>
      </Field>
    )}
  </SectionShell>
);

export default ChecklistSection;
